import {
  Bot,
  Brain,
  CheckCircle2,
  Megaphone,
  Package,
  Radio,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { AppHeader } from "@/components/sartn/AppHeader";
import { cn } from "@/lib/cn";
import { useLanguageStore } from "@/stores/languageStore";

const HUB_ITEMS: GrowthHubItem[] = [
  {
    title: "Products first",
    titleAm: "መጀመሪያ ምርቶች",
    description: "Add your catalog once so the bot, channels, groups, and campaigns use the same source.",
    descriptionAm: "ቦት፣ ቻናል፣ ቡድንና ካምፔይኖች አንድ መረጃ እንዲጠቀሙ የምርት ዝርዝርዎን አንዴ ያክሉ።",
    path: "/business",
    icon: Package,
    tone: "bg-emerald-50 text-emerald-600",
  },
  {
    title: "Marketing",
    titleAm: "ግብይት",
    description: "Create campaigns and AI-written customer messages.",
    descriptionAm: "ካምፔይኖችን እና AI የጻፋቸውን የደንበኛ መልዕክቶች ይፍጠሩ።",
    path: "/marketing",
    icon: Megaphone,
    tone: "bg-rose-50 text-rose-600",
  },
  {
    title: "AI Agents",
    titleAm: "AI ወኪሎች",
    description: "Use sales, support, marketing, and research assistants.",
    descriptionAm: "የሽያጭ፣ ድጋፍ፣ ግብይት እና ጥናት ረዳቶችን ይጠቀሙ።",
    path: "/marketing",
    icon: Bot,
    tone: "bg-violet-50 text-violet-600",
  },
  {
    title: "Intelligence",
    titleAm: "ትንተና",
    description: "Track competitors, alerts, and research reports.",
    descriptionAm: "ተወዳዳሪዎችን፣ ማስጠንቀቂያዎችን እና ሪፖርቶችን ይከታተሉ።",
    path: "/intelligence",
    icon: Brain,
    tone: "bg-purple-50 text-purple-600",
  },
  {
    title: "Channels",
    titleAm: "ቻናሎች",
    description: "Manage channel setup, posts, templates, and analytics.",
    descriptionAm: "የቻናል ማዘጋጃ፣ ፖስቶች፣ ቴምፕሌቶች እና ትንተና ያስተዳድሩ።",
    path: "/channels",
    icon: Radio,
    tone: "bg-indigo-50 text-indigo-600",
  },
  {
    title: "Groups",
    titleAm: "ቡድኖች",
    description: "Set welcome messages, moderation, warnings, and protection.",
    descriptionAm: "የእንኳን ደህና መጣችሁ፣ ቁጥጥር፣ ማስጠንቀቂያ እና ጥበቃ ያዘጋጁ።",
    path: "/groups",
    icon: Shield,
    tone: "bg-emerald-50 text-emerald-600",
  },
  {
    title: "Automations",
    titleAm: "አውቶሜሽን",
    description: "Build trigger-action workflows for replies and follow-ups.",
    descriptionAm: "ምላሾችን እና ክትትሎችን የሚሰሩ trigger-action ፍሰቶችን ይገንቡ።",
    path: "/automations",
    icon: Zap,
    tone: "bg-amber-50 text-amber-600",
  },
];

interface GrowthHubItem {
  title: string;
  titleAm: string;
  description: string;
  descriptionAm: string;
  path: string;
  icon: LucideIcon;
  tone: string;
}

function navigateTo(path: string) {
  window.location.hash = path;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

function GrowthCard({ item, index }: { item: GrowthHubItem; index: number }) {
  const Icon = item.icon;
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigateTo(item.path)}
      className="flex w-full gap-3 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4 text-left shadow-sm"
    >
      <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", item.tone)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-headline text-sm font-bold text-[var(--color-text)]">
            {localize(item.title, item.titleAm, language)}
          </h2>
          <span className="text-xs font-semibold text-[var(--color-primary)]">
            {localize("Open", "ክፈት", language)}
          </span>
        </div>
        <p className="mt-2 text-xs leading-5 text-[var(--color-text-secondary)]">
          {localize(item.description, item.descriptionAm, language)}
        </p>
      </div>
    </motion.button>
  );
}

export default function GrowthPage() {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="flex flex-1 flex-col bg-[var(--color-bg)]">
      <AppHeader title="Growth / እድገት" />

      <main className="flex-1 px-4 pb-24 pt-2">
        <section className="mb-4 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-5 text-white shadow-sm">
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15">
            <Sparkles className="h-5 w-5" />
          </div>
          <h1 className="font-headline text-xl font-bold">
            {localize("Manage growth in the right order", "እድገትን በትክክለኛ ቅደም ተከተል ያስተዳድሩ", language)}
          </h1>
          <p className="mt-3 text-sm leading-6 text-white/80">
            {localize(
              "Start with products, connect Telegram places, then run campaigns and automations.",
              "በምርቶች ይጀምሩ፣ የቴሌግራም ቦታዎችን ያገናኙ፣ ከዚያ ካምፔይኖችና አውቶሜሽኖችን ያስኪዱ።",
              language,
            )}
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] font-semibold text-white/85">
            {[
              localize("Products", "ምርቶች", language),
              localize("Connect", "አገናኝ", language),
              localize("Promote", "አስተዋውቅ", language),
            ].map((label) => (
              <span key={label} className="flex items-center gap-1 rounded-xl bg-white/10 px-2 py-2">
                <CheckCircle2 className="h-3 w-3" />
                {label}
              </span>
            ))}
          </div>
        </section>

        <div className="flex flex-col gap-3">
          {HUB_ITEMS.map((item, index) => (
            <GrowthCard key={item.title} item={item} index={index} />
          ))}
        </div>
      </main>
    </div>
  );
}
